/**
 * orbitsPipeline — a small solar system simulation.
 *
 * A central sun with several planets on elliptical orbits. Each
 * planet leaves a fading trail. New moons orbit the largest planet.
 * Stars fill the background.
 *
 * The simulation is computed on the GPU per frame: for each planet,
 * we evaluate Kepler's laws to find its position at the current time,
 * then render it as a glowing point with a colored glow.
 *
 * Audio:
 *   - bass: orbital speed
 *   - mid:  trail length
 *   - high: sun brightness + sparkle
 *
 * Single fullscreen quad fragment shader.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface OrbitsPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

float hash21(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

// Position of planet i at time t. Returns (x, y, radius, color.r, color.g, color.b).
// We use Kepler's equation for elliptical orbits.
struct Planet {
  vec2 pos;
  float radius;
  vec3 color;
};

Planet planetAt(int i, float t) {
  // Different orbital elements for each planet.
  // semi-major axis, eccentricity, period, phase, radius, color.
  float a, e, period, phase, radius;
  vec3 color;
  if (i == 0) {
    a = 0.20; e = 0.05; period = 2.5; phase = 0.0; radius = 0.012;
    color = vec3(0.95, 0.85, 0.75);
  } else if (i == 1) {
    a = 0.32; e = 0.10; period = 4.5; phase = 1.2; radius = 0.018;
    color = vec3(1.0, 0.65, 0.40);
  } else if (i == 2) {
    a = 0.46; e = 0.18; period = 7.0; phase = 2.4; radius = 0.022;
    color = vec3(0.50, 0.75, 1.0);
  } else if (i == 3) {
    a = 0.62; e = 0.07; period = 11.0; phase = 3.6; radius = 0.028;
    color = vec3(0.85, 0.55, 0.35);
  } else {
    a = 0.82; e = 0.22; period = 17.0; phase = 4.8; radius = 0.024;
    color = vec3(0.90, 0.80, 0.60);
  }
  // Mean anomaly.
  float M = mod(t / period * 6.2831853 + phase, 6.2831853);
  // Solve Kepler's equation: M = E - e * sin(E).
  float E = M;
  for (int j = 0; j < 4; j++) {
    E = M + e * sin(E);
  }
  // Position in orbital plane.
  float xOrbit = a * (cos(E) - e);
  float yOrbit = a * sqrt(1.0 - e * e) * sin(E);
  // Apply inclination (tilt of orbit).
  float incl = float(i) * 0.15;
  mat2 rotIncl = mat2(cos(incl), -sin(incl), sin(incl), cos(incl));
  vec2 orbitPos = rotIncl * vec2(xOrbit, yOrbit);
  // Rotate the whole system slowly.
  float sysRot = t * 0.05;
  mat2 rotSys = mat2(cos(sysRot), -sin(sysRot), sin(sysRot), cos(sysRot));
  vec2 pos = rotSys * orbitPos;
  Planet p;
  p.pos = pos;
  p.radius = radius;
  p.color = color;
  return p;
}

void main() {
  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid = pow(clamp(mid * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  // Speed up time slightly with bass.
  float t = uTime * (1.0 + bass * 0.5);

  // Aspect-corrected coords, centered.
  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / uResolution.y;
  // Slight upward shift to leave room for the sun's halo.
  p.y -= 0.05;

  // Background: deep space.
  vec3 bg = vec3(0.005, 0.005, 0.015);
  // Faint stars.
  vec2 starUv = floor(gl_FragCoord.xy / 2.0);
  float starSeed = hash21(starUv);
  float starMask = step(0.998, starSeed);
  float starBright = hash21(starUv + 1.0);
  bg += vec3(0.9, 0.95, 1.0) * starMask * (0.4 + 0.6 * starBright);

  // Subtle nebula tint.
  bg += vec3(0.08, 0.04, 0.15) * 0.05;

  vec3 col = bg;

  // Central sun — bright white/yellow with corona.
  float sunDist = length(p);
  float sunGlow = exp(-sunDist * 8.0) * (1.0 + high * 1.5);
  col += vec3(1.0, 0.95, 0.7) * sunGlow * (1.5 + bass * 0.5);
  // Sun core (very bright).
  float sunCore = smoothstep(0.05, 0.0, sunDist);
  col += vec3(1.0, 1.0, 0.9) * sunCore * 2.0;
  // Outer corona (subtle).
  col += vec3(1.0, 0.7, 0.4) * exp(-sunDist * 3.0) * 0.15 * (1.0 + high * 0.5);

  // Compute planet positions for all 5 planets.
  const int N = 5;
  Planet planets[5];
  for (int i = 0; i < N; i++) {
    planets[i] = planetAt(i, t);
  }

  // Draw orbital paths (faint ellipses) by sampling the planet
  // positions over many time steps.
  // For each pixel, find the closest planet-orbital-line.
  // Approximation: for each planet, find min distance to its
  // current and past positions (forming an arc).
  int trailSteps = 24 + int(mid * 24.0);
  for (int pIdx = 0; pIdx < N; pIdx++) {
    float minDist = 1e9;
    for (int s = 0; s < 32; s++) {
      if (s >= trailSteps) break;
      float tStep = t - float(s) * 0.05;
      Planet past = planetAt(pIdx, tStep);
      float d = length(p - past.pos);
      minDist = min(minDist, d);
    }
    // Trail: thin band along the orbital path.
    float trail = exp(-minDist * minDist * 1000.0);
    vec3 trailColor = planets[pIdx].color * 0.5;
    float fade = 1.0 - pow(float(pIdx) / float(N), 1.5) * 0.5;
    col += trailColor * trail * fade * 0.4;
  }

  // Draw planets as glowing points.
  for (int i = 0; i < N; i++) {
    vec2 pp = planets[i].pos;
    float d = length(p - pp);
    // Bright core.
    float core = exp(-d * d * 3000.0);
    col += planets[i].color * core * 2.0;
    // Outer glow.
    float radius = planets[i].radius;
    float glow = exp(-d * d / (radius * radius * 0.25));
    col += planets[i].color * glow * 0.8 * (0.7 + high * 0.5);
  }

  // Tone mapping (ACES-approximation).
  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));

  outColor = vec4(col, 1.0);
}
`;

export function computeOrbitsBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.3);
  out[1] = 0.5 + 0.3 * Math.sin(t * 0.5 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 0.8 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.0 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.2 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 1.5 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 1.9 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.3 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 2.8 + 3.0);
}

export function computeOrbitsRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createOrbitsPipeline(gl: WebGL2RenderingContext): OrbitsPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawOrbits(
  gl: WebGL2RenderingContext,
  pipe: OrbitsPipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
